import { countByAsRecord } from 'lib/array/arrayUtils'

import { isBookableTripListRow } from './tripListRows'
import type { TripListLocation, TripListRow, TripListRowType, TripListStatusValue } from './tripListTypes'

export const TRIP_LIST_TYPE_FILTERS: ReadonlyArray<TripListRowType> = [
  'flight',
  'accommodation',
  'experience',
  'cruise',
  'activity',
  'dining',
  'transport',
]

export const TRIP_LIST_STATUS_FILTERS: ReadonlyArray<TripListStatusValue> = ['IDEA', 'BOOKED', 'NOT_BOOKED']

export interface TripListLocationFilterOption {
  location: TripListLocation
  count: number
  firstIndex: number
}

export interface TripListDateFilterOption {
  value: string
  label: string
  count: number
  firstIndex: number
}

export function getTripListDateFilterOptions(rows: Array<TripListRow>): Array<TripListDateFilterOption> {
  const options = new Map<string, TripListDateFilterOption>()

  rows.forEach((row, index) => {
    const date = row.day?.date
    if (!date) return

    const existing = options.get(date)
    if (existing) {
      existing.count += 1
      return
    }

    options.set(date, {
      value: date,
      label: row.dayLabel,
      count: 1,
      firstIndex: index,
    })
  })

  return Array.from(options.values()).sort((a, b) => a.firstIndex - b.firstIndex)
}

export function getTripListLocationFilterOptions(rows: Array<TripListRow>): Array<TripListLocationFilterOption> {
  const options = new Map<string, TripListLocationFilterOption>()

  rows.forEach((row, index) => {
    if (row.kind !== 'item') return

    const existing = options.get(row.stopLocation.filterValue)
    if (existing) {
      existing.count += 1
      return
    }

    options.set(row.stopLocation.filterValue, {
      location: row.stopLocation,
      count: 1,
      firstIndex: index,
    })
  })

  return Array.from(options.values()).sort((a, b) => a.firstIndex - b.firstIndex)
}

export function getTripListRowCountByType(rows: Array<TripListRow>): Record<TripListRowType, number> {
  return countByAsRecord(rows, row => row.rowType)
}

export function getTripListTypeFilterOptions(rowCountByType: Record<TripListRowType, number>): Array<TripListRowType> {
  return TRIP_LIST_TYPE_FILTERS.filter(type => (rowCountByType[type] ?? 0) > 0)
}

export function getTripListRowCountByStatus(rows: Array<TripListRow>): Record<TripListStatusValue, number> {
  return countByAsRecord(rows, row => row.status.value)
}

export function getTripListStatusFilterOptions(rowCountByStatus: Record<TripListStatusValue, number>): Array<TripListStatusValue> {
  return TRIP_LIST_STATUS_FILTERS.filter(status => (rowCountByStatus[status] ?? 0) > 0)
}

function getTripListBookableRowCount(rows: Array<TripListRow>): number {
  return rows.reduce((count, row) => (
    isBookableTripListRow(row) ? count + 1 : count
  ), 0)
}

export function hasTripListDateFilterOptions(rows: Array<TripListRow>): boolean {
  const options = getTripListDateFilterOptions(rows)
  if (options.length > 1) return true
  if (options.length === 0) return false

  const onlyDate = options[0].value
  return rows.some(row => row.day?.date !== onlyDate)
}

export function hasTripListLocationFilterOptions(rows: Array<TripListRow>): boolean {
  return getTripListLocationFilterOptions(rows).length > 1
}

export function hasTripListStatusFilterOptions(rows: Array<TripListRow>): boolean {
  return getTripListStatusFilterOptions(getTripListRowCountByStatus(rows)).length > 1
}

export function hasTripListBookableOnlyFilterOption(rows: Array<TripListRow>): boolean {
  const bookableRowCount = getTripListBookableRowCount(rows)
  return bookableRowCount > 0 && bookableRowCount < rows.length
}

export function hasTripListTypeFilterOptions(rows: Array<TripListRow>): boolean {
  const typeOptions = getTripListTypeFilterOptions(getTripListRowCountByType(rows))
  return typeOptions.length > 1 || hasTripListBookableOnlyFilterOption(rows)
}

export function hasAnyTripListFilterOptions(rows: Array<TripListRow>): boolean {
  return (
    hasTripListDateFilterOptions(rows) ||
    hasTripListLocationFilterOptions(rows) ||
    hasTripListTypeFilterOptions(rows) ||
    hasTripListStatusFilterOptions(rows)
  )
}
